import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import MediaUploader from "../common/MediaUploader";
import CategorySelect from "./CategorySelect";
import { METHOD, CENTER, todayLocal } from "./constants";
import DatePicker from "../common/DatePicker";

const blank = () => ({ category: "", amount: "", date: todayLocal(), payment_method: "nakit", cost_center: "genel", description: "", media: [], recurring: false, day_of_month: new Date().getDate() > 28 ? 28 : new Date().getDate() });

const SimpleSelect = ({ value, onChange, options, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>{Object.entries(options).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
  </Select>
);

export default function ExpenseFormDialog({ open, onOpenChange, editing, categories, onCategoriesChanged, onSaved, canManage = true }) {
  const [form, setForm] = useState(blank());
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setForm(editing ? { ...blank(), ...editing, date: (editing.date || "").slice(0, 10), media: editing.media || [] } : blank());
  }, [open, editing]);

  const submit = async () => {
    if (!form.category) return toast.error("Kategori seçin");
    if (!(Number(form.amount) > 0)) return toast.error("Geçerli bir tutar girin");
    setBusy(true);
    const payload = { category: form.category, amount: Number(form.amount), description: form.description, payment_method: form.payment_method, cost_center: form.cost_center, media: form.media, date: form.date ? new Date(`${form.date}T12:00:00`).toISOString() : null, recurring_id: editing?.recurring_id || null };
    try {
      if (!editing && canManage && form.recurring) {
        const { data } = await api.post("/recurring-expenses", { category: form.category, amount: payload.amount, description: form.description, payment_method: form.payment_method, cost_center: form.cost_center, day_of_month: Number(form.day_of_month), active: true, skip_current_month: true });
        payload.recurring_id = data.id;
      }
      if (editing) await api.put(`/expenses/${editing.id}`, payload);
      else await api.post("/expenses", payload);
      toast.success(editing ? "Gider güncellendi" : "Gider kaydedildi");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kayıt başarısız"); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto" data-testid="expense-dialog">
        <DialogHeader><DialogTitle>{editing ? "Gideri Düzenle" : "Yeni Gider"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2"><Label>Kategori</Label><CategorySelect value={form.category} onChange={set("category")} categories={categories} onAdded={onCategoriesChanged} /></div>
          <div><Label>Tutar (₺)</Label><Input type="number" inputMode="decimal" min="0" step="0.01" value={form.amount} onChange={(e) => set("amount")(e.target.value)} data-testid="exp-amount" /></div>
          <div><Label>Tarih</Label><DatePicker value={form.date} onChange={set("date")} testid="exp-date" /></div>
          <div><Label>Ödeme Yöntemi</Label><SimpleSelect value={form.payment_method} onChange={set("payment_method")} options={METHOD} testid="exp-method" /></div>
          <div><Label>Masraf Merkezi</Label><SimpleSelect value={form.cost_center} onChange={set("cost_center")} options={CENTER} testid="exp-center" /></div>
          <div className="sm:col-span-2"><Label>Açıklama</Label><Textarea value={form.description} onChange={(e) => set("description")(e.target.value)} placeholder="Örn: Haziran dükkan kirası" data-testid="exp-desc" /></div>
          <div className="sm:col-span-2"><Label>Fiş / Fatura Fotoğrafı</Label><MediaUploader value={form.media} onChange={set("media")} testid="exp-media" /></div>
          {!editing && canManage && (
            <div className="sm:col-span-2 rounded-xl border border-slate-200 p-3 space-y-2">
              <label className="flex items-center justify-between gap-3">
                <span className="text-sm font-semibold">Her ay tekrarla</span>
                <Switch checked={form.recurring} onCheckedChange={set("recurring")} data-testid="exp-recurring-switch" />
              </label>
              {form.recurring && (
                <div className="flex items-center gap-2 text-sm">
                  <span>Her ayın</span>
                  <Input type="number" min="1" max="28" className="w-20" value={form.day_of_month} onChange={(e) => set("day_of_month")(e.target.value)} data-testid="exp-recurring-day" />
                  <span>. günü otomatik oluşturulsun</span>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="exp-cancel-btn">İptal</Button>
          <Button disabled={busy} className="bg-[#1B4D3E] hover:bg-[#163e32]" onClick={submit} data-testid="exp-save-btn">{busy ? "Kaydediliyor..." : "Kaydet"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
